import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useMemo, useState } from 'react';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { useMatchXg } from '@/features/matches/hooks';

/** One highlight clip as the provider sends it (event-detail `highlights[]`). */
interface Highlight {
  kind: string;
  title: string;
  url: string;
  thumbnail: string;
  published_at: string;
}

const CARD_WIDTH = 232;
const THUMB_HEIGHT = 130;

/** A single video card: thumbnail + a play glyph overlay + a 2-line title. Tapping
 *  opens the external clip (YouTube) in the system browser / app. */
function HighlightCard({ clip }: { clip: Highlight }) {
  const palette = useThemePalette();
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumb = Boolean(clip.thumbnail) && !thumbFailed;

  const open = () => {
    // Only follow http(s) links; never hand the OS a javascript:/intent:/file:
    // scheme from upstream data.
    if (!clip.url || !/^https?:\/\//i.test(clip.url)) return;
    // openURL is best-effort: a malformed / unsupported link simply no-ops rather
    // than throwing into the render tree.
    void Linking.openURL(clip.url).catch(() => {
      // Swallow: nothing actionable to show the user for a bad external link.
    });
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={clip.title}
      onPress={open}
      style={{ width: CARD_WIDTH }}
      className="overflow-hidden rounded-card border border-glass-border bg-glass active:opacity-80"
    >
      <View className="relative overflow-hidden bg-inset" style={{ height: THUMB_HEIGHT }}>
        {showThumb ? (
          <Image
            source={{ uri: clip.thumbnail }}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={200}
            onError={() => setThumbFailed(true)}
          />
        ) : (
          <View className="flex-1 items-center justify-center">
            <Ionicons name="film-outline" size={30} color={palette.muted} />
          </View>
        )}
        {/* Scrim under the play button so the glyph reads on any thumbnail. */}
        <View
          className="absolute inset-0 items-center justify-center"
          pointerEvents="none"
        >
          <View
            className="h-11 w-11 items-center justify-center rounded-full"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)', borderWidth: 1.5, borderColor: palette.voltBright }}
          >
            <Ionicons name="play" size={18} color={palette.voltBright} style={{ marginStart: 2 }} />
          </View>
        </View>
      </View>
      <View className="px-3 py-2.5">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={2}>
          {clip.title}
        </AppText>
      </View>
    </Pressable>
  );
}

/**
 * Highlights section: a horizontally-scrolling rail of video cards off the
 * Bzzoiro event-detail `highlights[]` (press-conference clips pre-game, match
 * clips post-game). Newest first. Self-hides entirely when the slice is empty /
 * null, so an unsynced or pre-press-conference fixture renders nothing.
 */
export function MatchHighlights({ fixtureId }: { fixtureId: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { data: xg } = useMatchXg(fixtureId);

  const clips = useMemo<Highlight[]>(() => {
    const raw = xg?.stats?.highlights ?? null;
    if (!raw || raw.length === 0) return [];
    // Newest first; published_at is an ISO string, so a string compare suffices,
    // but Date keeps it correct across mixed offsets.
    return [...raw]
      .filter((clip) => Boolean(clip?.url))
      .sort(
        (a, b) =>
          new Date(b.published_at).getTime() - new Date(a.published_at).getTime(),
      );
  }, [xg?.stats?.highlights]);

  if (clips.length === 0) return null;

  return (
    <View className="mb-4">
      <View className="mb-2.5 flex-row items-center gap-2 px-4">
        <Ionicons name="play-circle-outline" size={16} color={palette.voltBright} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('highlights.title')}
        </AppText>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 12 }}
      >
        {clips.map((clip, index) => (
          <HighlightCard key={`${clip.url}-${index}`} clip={clip} />
        ))}
      </ScrollView>
    </View>
  );
}
